/** Members: invite clients/users, project grants, resend the invitation, deactivate. */
import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { api } from "../api/client";
import { keys, useMe, useMembers, useOrg, useOrgs, useProjects } from "../api/hooks";
import type { Member, ProjectCard } from "../api/types";
import { can } from "../components/AppShell";
import { StageIntro } from "../components/sat";
import { ConfirmDialog, ErrorNotice, Field, Notice, Spinner } from "../components/ui";
import { dateTime } from "../lib/format";

function ProjectGrants({ projects, value, onChange, disabled }: { projects: ProjectCard[]; value: string[]; onChange: (next: string[]) => void; disabled?: boolean }) {
  if (!projects.length) return <span className="muted small">No projects yet.</span>;
  return (
    <div className="stack" style={{ gap: "0.25rem" }}>
      {projects.map((project) => (
        <label className="checkbox small" key={project.id}>
          <input
            type="checkbox"
            disabled={disabled}
            checked={value.includes(project.id)}
            onChange={(event) => onChange(event.target.checked ? [...value, project.id] : value.filter((id) => id !== project.id))}
          />
          <span>{project.label}</span>
        </label>
      ))}
    </div>
  );
}

function MemberRow({ org, member, projects, editable, timeZone }: { org: string; member: Member; projects: ProjectCard[]; editable: boolean; timeZone: string }) {
  const client = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [resent, setResent] = useState(false);
  const refresh = () =>
    Promise.all([client.invalidateQueries({ queryKey: keys.members(org) }), client.invalidateQueries({ queryKey: keys.org(org) }), client.invalidateQueries({ queryKey: keys.orgs })]);

  async function patch(body: Record<string, unknown>) {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/orgs/${org}/members/${member.uid}`, { version: member.version, ...body });
      await refresh();
    } catch (reason) {
      setError(reason);
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr>
      <td>
        <strong>{member.name ?? member.email}</strong>
        {member.name && <div className="muted small">{member.email}</div>}
        {member.invited_at && <div className="muted small">Invited {dateTime(member.invited_at, timeZone)}</div>}
        <ErrorNotice error={error} />
        {resent && <div className="small" role="status">Invitation e-mail sent.</div>}
      </td>
      <td>{member.role === "user" ? "User" : "Client (read-only)"}</td>
      <td>
        {member.role === "client" ? (
          <ProjectGrants projects={projects} value={member.projects} disabled={!editable || busy || !member.active} onChange={(next) => patch({ projects: next })} />
        ) : (
          <span className="muted small">All projects of the organisation</span>
        )}
      </td>
      <td>
        <span className={`badge ${member.active ? "pass" : "neutral"}`}>{member.active ? "Active" : "Deactivated"}</span>
      </td>
      <td>
        {editable && (
          <div className="row">
            {member.active && (
              <button
                className="btn small"
                disabled={busy}
                onClick={async () => {
                  setError(null);
                  try {
                    await api.post(`/orgs/${org}/members/${member.uid}/resend-invite`);
                    setResent(true);
                  } catch (reason) {
                    setError(reason);
                  }
                }}
              >
                Resend invite
              </button>
            )}
            {member.active ? (
              <ConfirmDialog
                trigger={<button className="btn small danger">Deactivate</button>}
                title={`Deactivate ${member.email}?`}
                description="The account can no longer sign in. Access ends within a minute. You can reactivate it later."
                confirmLabel="Deactivate"
                danger
                onConfirm={() => patch({ active: false })}
              />
            ) : (
              <button className="btn small" disabled={busy} onClick={() => patch({ active: true })}>
                Reactivate
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

function InviteForm({ org, projects, canInviteUsers }: { org: string; projects: ProjectCard[]; canInviteUsers: boolean }) {
  const client = useQueryClient();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<"client" | "user">("client");
  const [grants, setGrants] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [sent, setSent] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setSent(null);
    try {
      await api.post(`/orgs/${org}/members`, { email: email.trim(), role, name: name.trim() || null, projects: role === "client" ? grants : [] });
      setSent(email.trim());
      setEmail("");
      setName("");
      setGrants([]);
      await Promise.all([client.invalidateQueries({ queryKey: keys.members(org) }), client.invalidateQueries({ queryKey: keys.org(org) }), client.invalidateQueries({ queryKey: keys.orgs })]);
    } catch (reason) {
      setError(reason);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card stack" onSubmit={submit}>
      <div className="card-head">
        <div>
          <h3>Invite someone</h3>
          <p>They receive an e-mail from noreply@nsgs.dk with a link to set their password.</p>
        </div>
      </div>
      <div className="grid-3">
        <Field label="E-mail address">
          <input className="input" type="email" required value={email} onChange={(event) => setEmail(event.target.value)} />
        </Field>
        <Field label="Name (optional)">
          <input className="input" maxLength={120} value={name} onChange={(event) => setName(event.target.value)} />
        </Field>
        <Field label="Role" hint={canInviteUsers ? "Users take a licence seat; clients do not." : "Users are added by NSGS."}>
          <select className="input" value={role} onChange={(event) => setRole(event.target.value as "client" | "user")}>
            <option value="client">Client (read-only)</option>
            {canInviteUsers && <option value="user">User</option>}
          </select>
        </Field>
      </div>
      {role === "client" && (
        <div>
          <strong className="small">Projects the client can see</strong>
          <ProjectGrants projects={projects} value={grants} onChange={setGrants} />
        </div>
      )}
      <ErrorNotice error={error} title="The invitation could not be sent" />
      {sent && <Notice kind="success">Invitation sent to {sent}.</Notice>}
      <div className="row end">
        <button className="btn primary" disabled={busy}>
          {busy ? "Inviting…" : "Send invitation"}
        </button>
      </div>
    </form>
  );
}

export function MembersPage() {
  const me = useMe();
  const [params, setParams] = useSearchParams();
  const isAdmin = me.data?.role === "admin";
  const orgs = useOrgs(isAdmin);
  const ownOrg = (me.data?.org as { id?: string } | null | undefined)?.id;
  const org = isAdmin ? (params.get("org") ?? orgs.data?.[0]?.id ?? null) : (ownOrg ?? null);
  const orgDoc = useOrg(org);
  const members = useMembers(org);
  const projects = useProjects();

  if (me.data && !can(me.data, "members.clients")) return <Notice kind="info">Members are managed by the project team.</Notice>;
  if (isAdmin && orgs.data && orgs.data.length === 0) return <Notice kind="info">Create an organisation first (Organisations).</Notice>;
  if (!org || members.isLoading || orgDoc.isLoading) return <Spinner label="Loading members" />;
  if (members.error || orgDoc.error) return <ErrorNotice error={members.error ?? orgDoc.error} />;

  const licence = orgDoc.data!.licence;
  const canInviteUsers = isAdmin || can(me.data, "members.users");
  const orgProjects = (projects.data ?? []).filter((project) => project.org_id === org);
  const timeZone = String((orgDoc.data!.defaults as { timezone?: string }).timezone ?? "Europe/Copenhagen");
  const users = members.data!.filter((member) => member.role === "user");
  const clients = members.data!.filter((member) => member.role === "client");

  const table = (rows: Member[], editable: boolean, empty: string) =>
    rows.length === 0 ? (
      <p className="muted small">{empty}</p>
    ) : (
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Person</th>
              <th>Role</th>
              <th>Projects</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((member) => (
              <MemberRow key={member.uid} org={org} member={member} projects={orgProjects} editable={editable} timeZone={timeZone} />
            ))}
          </tbody>
        </table>
      </div>
    );

  return (
    <div className="stack" style={{ gap: "1.4rem" }}>
      <StageIntro
        title="Members and clients"
        description="Users work on every project of the organisation. Clients see the projects you grant them, read-only."
        status={licence.type === "seat" ? `${orgDoc.data!.seats_used} of ${licence.seats} user seats` : "Unlimited users"}
      />
      {isAdmin && (
        <Field label="Organisation">
          <select className="input" style={{ maxWidth: 360 }} value={org} onChange={(event) => setParams({ org: event.target.value })}>
            {orgs.data?.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </Field>
      )}
      <InviteForm key={org} org={org} projects={orgProjects} canInviteUsers={canInviteUsers} />
      <section className="stack">
        <h3>Users</h3>
        {table(users, canInviteUsers, "No users yet.")}
      </section>
      <section className="stack">
        <h3>Clients</h3>
        {table(clients, true, "No clients yet. Invite a client to share saved results and reports.")}
      </section>
    </div>
  );
}
